import React, { useState, useEffect } from 'react';
import { Card, Button } from '../ui';
import { useApp } from '../../contexts/AppContext';
import { useAuth } from '../../contexts/AuthContext';
import { subscribeToCredits, testTopUp } from '../../services/creditService';
import { MessagingCredits } from '../../types';
import CreditRequestDialog from './CreditRequestDialog';

interface CreditBalanceProps {
  compact?: boolean;
  showToast?: (message: string, type: 'success' | 'error' | 'info') => void;
}

const CreditBalance: React.FC<CreditBalanceProps> = ({ compact = false, showToast }) => {
  const { selectedOrganization } = useApp();
  const { userData } = useAuth();
  const [credits, setCredits] = useState<MessagingCredits | null>(null);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [topUpLoading, setTopUpLoading] = useState(false);

  useEffect(() => {
    if (!selectedOrganization?.id) return;

    const unsubscribe = subscribeToCredits(selectedOrganization.id, setCredits);
    return () => unsubscribe();
  }, [selectedOrganization?.id]);

  const handleTestTopUp = async (amount: number) => {
    if (!selectedOrganization?.id || !userData) return;
    setTopUpLoading(true);
    try {
      await testTopUp(selectedOrganization.id, amount, {
        userId: userData.id,
        name: userData.name || userData.email
      });
      showToast?.(`Added ${amount} test credits`, 'success');
    } catch (error) {
      console.error('Test top-up failed:', error);
      showToast?.('Failed to add test credits', 'error');
    } finally {
      setTopUpLoading(false);
    }
  };

  if (!credits) {
    if (compact) {
      return (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-secondary-300" />
            <span className="text-sm font-medium text-secondary-500">
              No credits
            </span>
          </div>
          <button
            onClick={() => setShowRequestDialog(true)}
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            Request Credits
          </button>
          {showRequestDialog && (
            <CreditRequestDialog
              onClose={() => setShowRequestDialog(false)}
              showToast={showToast}
            />
          )}
        </div>
      );
    }
    return (
      <Card>
        <div className="p-4 sm:p-6">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900">Message Credits</h3>
            <p className="text-sm text-secondary-500 mt-1">
              No credits yet. Top up to start sending messages.
            </p>
          </div>
          <div className="mt-4 flex gap-2">
            {[10, 50, 100].map((amount) => (
              <Button
                key={amount}
                size="sm"
                variant="outline"
                onClick={() => handleTestTopUp(amount)}
                disabled={topUpLoading}
              >
                +{amount} credits
              </Button>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  const balance = credits.messageCredits;
  const threshold = credits.lowCreditThreshold || 10;
  const isLow = balance <= threshold && balance > 0;
  const isEmpty = balance <= 0;

  const getBarColor = () => {
    if (isEmpty) return 'bg-red-500';
    if (isLow) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getBarWidth = () => {
    if (isEmpty) return '0%';
    // Use threshold * 5 as "full" reference (arbitrary visual scale)
    const maxRef = Math.max(threshold * 5, balance);
    return `${Math.min((balance / maxRef) * 100, 100)}%`;
  };

  if (compact) {
    return (
      <>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${isEmpty ? 'bg-red-500' : isLow ? 'bg-yellow-500' : 'bg-green-500'}`} />
            <span className="text-sm font-medium text-secondary-700">
              {balance.toLocaleString()} credits
            </span>
          </div>
          {(isLow || isEmpty) && (
            <button
              onClick={() => setShowRequestDialog(true)}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              Get More
            </button>
          )}
        </div>

        {showRequestDialog && (
          <CreditRequestDialog
            onClose={() => setShowRequestDialog(false)}
            showToast={showToast}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Card>
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-secondary-900">Message Credits</h3>
            <div className="flex gap-2">
              {[10, 50, 100].map((amount) => (
                <Button
                  key={amount}
                  size="sm"
                  variant="outline"
                  onClick={() => handleTestTopUp(amount)}
                  disabled={topUpLoading}
                >
                  +{amount}
                </Button>
              ))}
            </div>
          </div>

          {/* Warning Banner */}
          {(isLow || isEmpty) && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
              isEmpty ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'
            }`}>
              <svg className={`w-5 h-5 flex-shrink-0 ${isEmpty ? 'text-red-600' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className={`text-sm font-medium ${isEmpty ? 'text-red-800' : 'text-yellow-800'}`}>
                {isEmpty
                  ? 'No credits remaining. You cannot send messages until credits are added.'
                  : `Low credits! Only ${balance} credit(s) remaining.`
                }
              </p>
            </div>
          )}

          {/* Balance Display */}
          <div className="space-y-3">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-secondary-900">{balance.toLocaleString()}</span>
              <span className="text-secondary-500">credits available</span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-secondary-100 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all ${getBarColor()}`}
                style={{ width: getBarWidth() }}
              />
            </div>

            <div className="flex justify-between text-sm text-secondary-500">
              <span>{credits.messagesUsed.toLocaleString()} messages sent (lifetime)</span>
              <span>Low threshold: {threshold}</span>
            </div>

            {credits.lastTopUpAt && credits.lastTopUpAmount && (
              <p className="text-xs text-secondary-400">
                Last top-up: {credits.lastTopUpAmount.toLocaleString()} credits on{' '}
                {credits.lastTopUpAt.toDate().toLocaleDateString()}
              </p>
            )}
          </div>

        </div>
      </Card>
    </>
  );
};

export default CreditBalance;
